import { Link } from "react-router-dom";
import { useEffect } from "react";
import JsonLd from "@/components/base/JsonLd";
import { articlesMeta, buildArticleJsonLd } from "@/mocks/seoMetadata";

const article = articlesMeta.find((a) => a.slug === "/articoli/automazione-itaobuy-spreadsheet")!;

export default function AutomazionePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleJsonLd(article)} />
      <header className="w-full px-6 md:px-10 lg:px-16 py-5 bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-2xl font-bold text-accent-500 tracking-tight">ItaoBuy</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-accent-600">
              itaobuy sheet
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-primary-600">
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
          </div>
        </div>
      </header>

      <article className="w-full px-6 md:px-10 lg:px-16 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-400 whitespace-nowrap">Automazione ItaoBuy Spreadsheet</span>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">Advanced Guide</span>
              <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">Automazione</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              Automazione Avanzata con ItaoBuy Spreadsheet e Google Apps Script
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              Scopri come automatizzare il tuo <strong>ItaoBuy Spreadsheet</strong> con Google Apps Script. Dall&apos;importazione automatica dei dati alle notifiche email, trasforma il tuo foglio in uno strumento intelligente che lavora per te.
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>Aggiornato: Luglio 2026</span>
              <span>|</span>
              <span>Tempo di lettura: 10 minuti</span>
            </div>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=Modern%20developer%20workspace%20with%20clean%20code%20visible%20on%20ultrawide%20monitor%20showing%20Google%20Apps%20Script%20editor%20interface%2C%20minimal%20desk%20accessories%20in%20warm%20cream%20tones%2C%20soft%20natural%20lighting%2C%20professional%20programming%20environment%20photography%2C%20elegant%20composition&width=1200&height=600&seq=article-automation-hero&orientation=landscape" alt="Automazione ItaoBuy Spreadsheet con Google Apps Script" title="Automazione Avanzata ItaoBuy Spreadsheet - Script e Automazione" className="w-full h-auto object-cover" />
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Perché Automatizzare ItaoBuy Spreadsheet</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              <strong>ItaoBuy Spreadsheet</strong> è già potente di per sé, ma con l&apos;automazione diventa uno strumento straordinario. Google Apps Script, il linguaggio di scripting integrato in Google Sheets, ti permette di creare flussi di lavoro automatici che risparmiano ore di lavoro manuale ogni settimana.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Pensa a quanto tempo passi copiando e incollando dati, inviando email di aggiornamento, o controllando manualmente i prezzi. L&apos;automazione elimina tutte queste attività ripetitive, permettendoti di concentrarti su decisioni strategiche di alto valore.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Script Essenziali per ItaoBuy Spreadsheet</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">1</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Importazione Automatica Dati</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Configura uno script che importa automaticamente i dati prodotti da URL esterni o da altri fogli di calcolo. Ogni volta che esegui lo script, il tuo <strong>ItaoBuy Spreadsheet</strong> si aggiorna con le ultime informazioni su prezzi, disponibilità e nuovi prodotti senza intervento manuale.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">2</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Notifiche Email Automatiche</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Imposta alert email che si attivano quando un prezzo scende sotto una soglia, quando uno stato ordine cambia, o quando è necessario un intervento umano. Ricevi notifiche direttamente nella tua casella Gmail senza dover controllare manualmente il foglio.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">3</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Report Periodici Personalizzati</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Genera report settimanali o mensili automaticamente con riepiloghi di spesa, prodotti più acquistati, tendenze di prezzo e KPI del tuo business. Lo script crea un nuovo foglio con il report formattato e pronto per la condivisione.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">4</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Backup Automatici su Drive</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Configura uno script che crea copie di backup del tuo <strong>ItaoBuy Spreadsheet</strong> su Google Drive con timestamp automatici. Mai più il rischio di perdere dati importanti per un errore accidentale.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10 p-6 md:p-8 bg-accent-500 rounded-2xl text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Pronto a Automatizzare il Tuo ItaoBuy Spreadsheet?</h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
              Inizia subito a utilizzare <strong className="text-white">ItaoBuy Spreadsheet</strong> e scopri come l&apos;automazione può rivoluzionare il tuo flusso di lavoro.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 px-6 py-3 bg-white text-accent-500 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/90">
                Visita itaobuy sheet <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
              </a>
              <Link to="/" className="flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/25">
                Torna alla Home <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              </Link>
            </div>
          </section>

          <nav className="flex items-center justify-between pt-6 border-t border-background-200/70">
            <Link to="/" className="flex items-center gap-2 text-sm text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span> ItaoBuy Spreadsheet Home
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-400">Condividi:</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su X"><i className="ri-twitter-x-line text-sm"></i></a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su Facebook"><i className="ri-facebook-fill text-sm"></i></a>
            </div>
          </nav>
        </div>
      </article>

      <footer className="w-full px-4 md:px-8 pt-4 pb-6 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-foreground-500">&copy; {new Date().getFullYear()} ItaoBuy Spreadsheet Italia. <Link to="/" className="text-accent-500 hover:text-accent-600 transition-colors cursor-pointer ml-1">Torna alla Home</Link></p>
        </div>
      </footer>
    </main>
  );
}